import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '@staff-app/ui';
import type { AdminUser } from '@staff-app/shared-types';
import { STAFF_ADMIN_USERS, STAFF_REENABLE_ADMIN_USER } from '@staff-app/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { AdminUsersTable } from '../components/AdminUsersTable';
import { InviteUserDialog } from '../components/InviteUserDialog';
import { EditRoleDialog } from '../components/EditRoleDialog';
import { DisableUserDialog } from '../components/DisableUserDialog';

interface ReenableResult {
  staffReenableAdminUser: AdminUser;
}

/**
 * Admin Users — root-only CRUD.
 *
 * Upstream routing guard: `<AdminRoleRoute requiredRole="root">` already
 * blocks non-root users. This component does a defensive re-check and
 * renders `<PendingApprovalPage />` if somehow a non-root user reaches it.
 */
export function AdminUsersPage() {
  const me = useAuthStore((s) => s.adminUser);

  const { data, loading, error } = useQuery<{ staffAdminUsers: AdminUser[] }>(STAFF_ADMIN_USERS, {
    fetchPolicy: 'cache-and-network',
  });

  const [reenableMutation] = useMutation<ReenableResult>(STAFF_REENABLE_ADMIN_USER, {
    refetchQueries: ['staffAdminUsers'],
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [disablingUser, setDisablingUser] = useState<AdminUser | null>(null);

  // Defensive: if somehow a non-root user reaches this page (e.g. direct URL),
  // render the pending-approval view instead of leaking data + actions.
  if (me?.role !== 'root') {
    return <PendingApprovalPage />;
  }

  const users = data?.staffAdminUsers ?? [];
  const rollup = summarizeRoles(users);

  const handleReenable = async (user: AdminUser) => {
    try {
      await reenableMutation({ variables: { email: user.email } });
      toast.success('User re-enabled');
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Unknown error';
      toast.error('Re-enable failed', { description: msg });
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{'Admin Users'}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {users.length} {'users'} · {rollup.root} {'root'} · {rollup.engineer}{' '}
            {'engineer'} · {rollup.support} {'support'} · {rollup.readonly} {'readonly'}
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          className="cursor-pointer"
          data-testid="invite-user-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          {'Invite user'}
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      ) : loading && !users.length ? (
        <div className="text-sm text-muted-foreground">{'Loading admin users…'}</div>
      ) : (
        <div className="rounded-lg border bg-card">
          <AdminUsersTable
            users={users}
            currentEmail={me.email}
            onEditRole={setEditingUser}
            onDisable={setDisablingUser}
            onReenable={handleReenable}
          />
        </div>
      )}

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <EditRoleDialog user={editingUser} onOpenChange={(o) => !o && setEditingUser(null)} />
      <DisableUserDialog user={disablingUser} onOpenChange={(o) => !o && setDisablingUser(null)} />
    </div>
  );
}

function summarizeRoles(users: AdminUser[]) {
  const rollup = { root: 0, engineer: 0, support: 0, readonly: 0 };
  for (const u of users) {
    if (u.role in rollup) rollup[u.role as keyof typeof rollup]++;
  }
  return rollup;
}
